/**
 * 답변 복사 — 한글·워드·카카오톡·나이스에 붙여 넣어도 마크다운 기호(`**`, `|---|`, `#`)가 보이지 않게
 * 같은 마크다운에서 읽기 좋은 글(text/plain)과 표·굵게가 살아 있는 HTML(text/html)을 함께 만든다.
 *
 * 파서는 `marked`(markdownBlocks.js 와 같은 것). unified/remark 는 vfile 의 `#minpath` 하위 경로 import 때문에
 * CRA Jest 에서 불러올 수 없어 실제 답변 모양으로 시험할 수 없다.
 */
import { Marked } from 'marked';
import { isMergeMarkerText } from '../components/EnhancedMarkdown/tableCellBreaks';
import { stripLeakedCitationMarkers } from './chatArtifactTags';
import { COPY_FENCE_LANG, parseCopyBlock } from './chatCopyBlocks';
import { isAnswerPartsEnabled } from './chatRichCatalog';

// remark-cjk-friendly 와 같은 목적: `**양분(녹말)**을`·`**"인용"**로` 처럼 문장부호로 끝난 굵게 뒤에 조사가 붙어도 굵게로 읽는다.
const CJK_STRONG_RE = /^\*\*(?![\s*])([^*\n]*?[^\s*])\*\*(?!\*)/;
const CJK_EM_RE = /^\*(?![\s*])([^*\n]*?[^\s*])\*(?!\*)/;
// 수식은 글자 그대로 둔다(`$a*b*c$` 의 `*` 가 기울임이 되지 않게).
const INLINE_MATH_RE = /^(?:\$(?![\s$])[^$\n]*?[^\s$\\]\$(?!\d)|\$[^\s$]\$(?!\d)|\\\([\s\S]+?\\\))/;
const BLOCK_MATH_RE = /^ {0,3}(?:\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\])[ \t]*(?:\n|$)/;

const indexOfAny = (src, needles) => {
  const found = needles.map((needle) => src.indexOf(needle)).filter((index) => index >= 0);
  return found.length ? Math.min(...found) : undefined;
};

const parser = new Marked({ gfm: true });
parser.use({
  extensions: [
    {
      name: 'blockMath',
      level: 'block',
      start: (src) => indexOfAny(src, ['$$', '\\[']),
      tokenizer(src) {
        const match = BLOCK_MATH_RE.exec(src);
        return match ? { type: 'blockMath', raw: match[0], text: match[0].trim() } : undefined;
      },
    },
    {
      name: 'inlineMath',
      level: 'inline',
      start: (src) => indexOfAny(src, ['$', '\\(']),
      tokenizer(src) {
        const match = INLINE_MATH_RE.exec(src);
        return match ? { type: 'inlineMath', raw: match[0], text: match[0] } : undefined;
      },
    },
    {
      name: 'cjkStrong',
      level: 'inline',
      start: (src) => indexOfAny(src, ['*']),
      tokenizer(src) {
        const strong = CJK_STRONG_RE.exec(src);
        if (strong) return { type: 'strong', raw: strong[0], text: strong[1], tokens: this.lexer.inlineTokens(strong[1]) };
        const em = CJK_EM_RE.exec(src);
        if (em) return { type: 'em', raw: em[0], text: em[1], tokens: this.lexer.inlineTokens(em[1]) };
        return undefined;
      },
    },
  ],
});

const PUA_RE = /[\ue200-\ue2ff]/g;
const BR_TAG_RE = /^<br\s*\/?>$/i;
const TAG_RE = /<[^>]+>/g;
const SAFE_HREF_RE = /^(?:https?:|mailto:)/i;

export const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

// marked 는 글자 토큰에 `&amp;` 같은 문자 참조를 그대로 둔다 — 붙여 넣을 글에서는 원래 글자로 돌린다.
const decodeEntities = (value) => String(value ?? '')
  .replace(/&(?:#(\d+)|#x([0-9a-f]+)|(amp|lt|gt|quot|#39|nbsp));/gi, (match, dec, hex, name) => {
    if (dec) return String.fromCodePoint(Number(dec));
    if (hex) return String.fromCodePoint(parseInt(hex, 16));
    return { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", nbsp: ' ' }[name.toLowerCase()];
  });

const bareUrl = (value) => String(value || '').trim().replace(/^mailto:/i, '').replace(/^https?:\/\//i, '').replace(/\/+$/, '');

// 링크는 글자와 주소가 다를 때만 `글자 (주소)`.
const linkPlain = (text, href) => {
  const label = text.trim();
  if (!href || !SAFE_HREF_RE.test(href)) return label;
  if (!label) return href;
  return bareUrl(label) === bareUrl(href) ? label : `${label} (${href})`;
};

const TABLE_BORDER = 'border:1px solid #bfbfbf;padding:6px 10px;';
const TABLE_OPEN = '<table border="1" cellspacing="0" cellpadding="6" style="border-collapse:collapse;">';
const cellStyle = (header, align) => `${TABLE_BORDER}${header ? 'background:#f2f2f2;font-weight:bold;' : 'vertical-align:top;'}text-align:${align || 'left'};`;

/* ---------- 글(text/plain) ---------- */

const inlinePlain = (tokens, ctx) => (tokens || []).map((token) => {
  switch (token.type) {
    case 'strong':
    case 'em':
    case 'del':
      return inlinePlain(token.tokens, ctx);
    case 'text':
      return token.tokens ? inlinePlain(token.tokens, ctx) : decodeEntities(token.text);
    case 'escape':
    case 'codespan':
      return decodeEntities(token.text);
    case 'inlineMath':
      return token.text;
    case 'br':
      return ctx.inTable ? ' ' : '\n';
    case 'link':
      return linkPlain(inlinePlain(token.tokens, ctx), token.href);
    case 'image':
      return linkPlain(decodeEntities(token.text), token.href);
    case 'html':
      if (BR_TAG_RE.test(token.text.trim())) return ctx.inTable ? ' ' : '\n';
      return decodeEntities(token.text.replace(TAG_RE, ''));
    default:
      return decodeEntities(token.text || '');
  }
}).join('');

const indentLines = (text, firstPrefix, restPrefix) => text
  .split('\n')
  .map((line, index) => (index === 0 ? firstPrefix + line : (line ? restPrefix + line : line)))
  .join('\n');

// 체크리스트(`- [ ] 할 일`)는 글머리표 없이 `□ 할 일` 줄로 — 한글 양식의 확인란 모양과 같다.
const isChecklist = (token) => isAnswerPartsEnabled()
  && !token.ordered && token.items.length > 0 && token.items.every((item) => item.task);
const checkMark = (item) => {
  if (item.checked) return '☑ ';
  return isAnswerPartsEnabled() ? '□ ' : '☐ ';
};

const blockPlain = (token) => {
  switch (token.type) {
    case 'paragraph':
    case 'heading':
      return inlinePlain(token.tokens, {});
    case 'text':
      return token.tokens ? inlinePlain(token.tokens, {}) : decodeEntities(token.text);
    case 'code': {
      if (String(token.lang || '').trim().toLowerCase() === COPY_FENCE_LANG) {
        const { title, body } = parseCopyBlock(token.text);
        const bodyPlain = markdownToPlain(body);
        return title ? `${title}\n\n${bodyPlain}` : bodyPlain;
      }
      return token.text;
    }
    case 'blockMath':
      return token.text;
    case 'blockquote':
      return blocksPlain(token.tokens);
    case 'list':
      if (isChecklist(token)) {
        return token.items.map((item) => indentLines(
          blocksPlain(item.tokens.filter((child) => child.type !== 'checkbox'), '\n'),
          checkMark(item),
          '  ',
        )).join('\n');
      }
      return token.items.map((item, index) => {
        const bullet = token.ordered ? `${Number(token.start || 1) + index}. ` : '- ';
        const check = item.task ? checkMark(item) : '';
        const body = blocksPlain(item.tokens.filter((child) => child.type !== 'checkbox'), '\n');
        return indentLines(body, bullet + check, ' '.repeat(bullet.length));
      }).join('\n');
    case 'table': {
      const row = (cells) => cells
        .map((cell) => (isMergeMarkerText(cell.text) ? '' : inlinePlain(cell.tokens, { inTable: true }).replace(/\s*\n\s*/g, ' ').trim()))
        .join('\t');
      return [row(token.header), ...token.rows.map(row)].join('\n');
    }
    case 'html':
      return decodeEntities(token.text.replace(TAG_RE, '')).trim();
    default:
      return '';
  }
};

const blocksPlain = (tokens, separator = '\n\n') => (tokens || [])
  .map(blockPlain)
  .filter((text) => text && text.trim())
  .join(separator);

const finishPlain = (text) => text
  .replace(PUA_RE, '')
  .replace(/[ \t]+$/gm, '')
  .replace(/\n{3,}/g, '\n\n')
  .trim();

/* ---------- HTML(text/html) ---------- */

const inlineHtml = (tokens, ctx) => (tokens || []).map((token) => {
  switch (token.type) {
    case 'strong':
      return `<strong>${inlineHtml(token.tokens, ctx)}</strong>`;
    case 'em':
      return `<em>${inlineHtml(token.tokens, ctx)}</em>`;
    case 'del':
      return `<del>${inlineHtml(token.tokens, ctx)}</del>`;
    case 'text': {
      if (token.tokens) return inlineHtml(token.tokens, ctx);
      const text = escapeHtml(decodeEntities(token.text));
      return ctx.hardBreaks ? text.replace(/\n/g, '<br>') : text;
    }
    case 'escape':
      return escapeHtml(decodeEntities(token.text));
    case 'codespan':
      return `<code>${escapeHtml(decodeEntities(token.text))}</code>`;
    case 'inlineMath':
      return escapeHtml(token.text);
    case 'br':
      return '<br>';
    case 'link':
    case 'image': {
      const label = token.type === 'image' ? escapeHtml(decodeEntities(token.text)) : inlineHtml(token.tokens, ctx);
      if (!token.href || !SAFE_HREF_RE.test(token.href)) return label;
      return `<a href="${escapeHtml(token.href)}">${label || escapeHtml(token.href)}</a>`;
    }
    case 'html':
      if (BR_TAG_RE.test(token.text.trim())) return '<br>';
      return escapeHtml(decodeEntities(token.text.replace(TAG_RE, '')));
    default:
      return escapeHtml(decodeEntities(token.text || ''));
  }
}).join('');

const blockHtml = (token, ctx) => {
  switch (token.type) {
    case 'paragraph':
      return `<p>${inlineHtml(token.tokens, ctx)}</p>`;
    case 'text':
      return token.tokens ? inlineHtml(token.tokens, ctx) : escapeHtml(decodeEntities(token.text));
    case 'heading': {
      const depth = Math.min(Math.max(Number(token.depth) || 2, 1), 6);
      return `<h${depth}>${inlineHtml(token.tokens, ctx)}</h${depth}>`;
    }
    case 'code': {
      if (String(token.lang || '').trim().toLowerCase() === COPY_FENCE_LANG) {
        const { title, body } = parseCopyBlock(token.text);
        const bodyHtml = markdownToHtml(body, { hardBreaks: true });
        return title ? `<p><strong>${escapeHtml(title)}</strong></p>\n${bodyHtml}` : bodyHtml;
      }
      return `<pre style="font-family:monospace;white-space:pre-wrap;"><code>${escapeHtml(token.text)}</code></pre>`;
    }
    case 'blockMath':
      return `<p>${escapeHtml(token.text)}</p>`;
    case 'blockquote':
      // 상자 이름표(`팁: …`, `한눈에 보기`)가 본문과 한 줄로 붙지 않게 인용 안 줄은 그대로 바꾼다.
      return `<blockquote style="margin:0 0 0 4px;padding-left:12px;border-left:3px solid #d0d7de;">${blocksHtml(token.tokens, { ...ctx, hardBreaks: true })}</blockquote>`;
    case 'list': {
      if (isChecklist(token)) {
        return token.items.map((item) => {
          const children = item.tokens.filter((child) => child.type !== 'checkbox');
          return `<div>${checkMark(item)}${children.map((child) => blockHtml(child, ctx)).join('')}</div>`;
        }).join('');
      }
      const tag = token.ordered ? 'ol' : 'ul';
      const start = token.ordered && Number(token.start) > 1 ? ` start="${Number(token.start)}"` : '';
      const items = token.items.map((item) => {
        const check = item.task ? checkMark(item) : '';
        const children = item.tokens.filter((child) => child.type !== 'checkbox');
        const body = item.loose ? blocksHtml(children, ctx) : children.map((child) => blockHtml(child, ctx)).join('');
        return `<li>${check}${body}</li>`;
      });
      return `<${tag}${start}>${items.join('')}</${tag}>`;
    }
    case 'table': {
      const cell = (tag, header) => (item, index) => (
        `<${tag} style="${cellStyle(header, token.align?.[index])}">${isMergeMarkerText(item.text) ? '' : inlineHtml(item.tokens, ctx)}</${tag}>`
      );
      const head = `<thead><tr>${token.header.map(cell('th', true)).join('')}</tr></thead>`;
      const body = token.rows.length
        ? `<tbody>${token.rows.map((row) => `<tr>${row.map(cell('td', false)).join('')}</tr>`).join('')}</tbody>`
        : '';
      return `${TABLE_OPEN}${head}${body}</table>`;
    }
    case 'hr':
      return '<hr>';
    case 'html': {
      const text = decodeEntities(token.text.replace(TAG_RE, '')).trim();
      return text ? `<p>${escapeHtml(text)}</p>` : '';
    }
    default:
      return '';
  }
};

const blocksHtml = (tokens, ctx) => (tokens || [])
  .map((token) => blockHtml(token, ctx))
  .filter(Boolean)
  .join('\n');

/* ---------- 바깥에 쓰는 함수 ---------- */

const lex = (markdown) => {
  const source = stripLeakedCitationMarkers(String(markdown || '').replace(/\r\n?/g, '\n'));
  try {
    return parser.lexer(source);
  } catch (error) {
    return [{ type: 'paragraph', tokens: [{ type: 'text', text: source }] }];
  }
};

export const markdownToPlain = (markdown) => finishPlain(blocksPlain(lex(markdown)));

export const markdownToHtml = (markdown, { hardBreaks = false } = {}) => (
  blocksHtml(lex(markdown), { hardBreaks }).replace(PUA_RE, '')
);

/** 답변 마크다운 → { plain, html }. plain 은 카톡·메모장·나이스용, html 은 한글·워드용. */
export const markdownToClipboard = (markdown, options = {}) => {
  const tokens = lex(markdown);
  return {
    plain: finishPlain(blocksPlain(tokens)),
    html: blocksHtml(tokens, { hardBreaks: Boolean(options.hardBreaks) }).replace(PUA_RE, ''),
  };
};

/* ---------- 화면에 그려진 표 → { plain, html } ---------- */

const domInline = (node, mode) => {
  if (node.nodeType === 3) return mode === 'html' ? escapeHtml(node.nodeValue) : node.nodeValue;
  if (node.nodeType !== 1) return '';
  const el = node;
  if (el.getAttribute('aria-hidden') === 'true') return '';
  if (el.classList.contains('katex')) {
    const tex = el.querySelector('annotation')?.textContent || el.textContent || '';
    return mode === 'html' ? escapeHtml(`$${tex}$`) : `$${tex}$`;
  }
  const tag = el.tagName.toLowerCase();
  if (tag === 'br') return mode === 'html' ? '<br>' : ' ';
  if (tag === 'button' || tag === 'svg') return '';
  const inner = Array.from(el.childNodes).map((child) => domInline(child, mode)).join('');
  if (mode !== 'html') return inner;
  if (tag === 'strong' || tag === 'b') return `<strong>${inner}</strong>`;
  if (tag === 'em' || tag === 'i') return `<em>${inner}</em>`;
  if (tag === 'del' || tag === 's') return `<del>${inner}</del>`;
  if (tag === 'code') return `<code>${inner}</code>`;
  return inner;
};

const cellAlign = (cell) => {
  const align = cell.getAttribute('align') || cell.style?.textAlign || '';
  return ['left', 'center', 'right'].includes(align) ? align : (cell.classList.contains('is-num') ? 'right' : '');
};

/** 그려진 <table> 의 칸 글자(줄마다 칸 배열). 칸 안 줄바꿈·연속 공백은 한 칸으로 줄인다. */
export const tableElementToRows = (table) => {
  if (!table) return [];
  return Array.from(table.querySelectorAll('tr')).map((row) => Array.from(row.children)
    .map((cell) => domInline(cell, 'plain').replace(PUA_RE, '').replace(/\s+/g, ' ').trim()));
};

/** 그려진 <table> 을 탭으로 나눈 글과 테두리 있는 HTML 표로 바꾼다. */
export const tableElementToClipboard = (table) => {
  if (!table) return { plain: '', html: '' };
  const rows = Array.from(table.querySelectorAll('tr'));
  const plain = tableElementToRows(table).map((cells) => cells.join('\t')).join('\n');
  const htmlRow = (row) => `<tr>${Array.from(row.children).map((cell) => {
    const tag = cell.tagName.toLowerCase() === 'th' ? 'th' : 'td';
    return `<${tag} style="${cellStyle(tag === 'th', cellAlign(cell))}">${domInline(cell, 'html').trim()}</${tag}>`;
  }).join('')}</tr>`;
  const headRows = Array.from(table.querySelectorAll('thead tr'));
  const bodyRows = rows.filter((row) => !headRows.includes(row));
  const html = [
    TABLE_OPEN,
    headRows.length ? `<thead>${headRows.map(htmlRow).join('')}</thead>` : '',
    bodyRows.length ? `<tbody>${bodyRows.map(htmlRow).join('')}</tbody>` : '',
    '</table>',
  ].join('').replace(PUA_RE, '');
  return { plain, html };
};

/* ---------- 클립보드 쓰기 ---------- */

const copyWithTextArea = (text) => {
  const previousFocus = document.activeElement;
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';
  document.body.appendChild(textArea);
  try {
    textArea.select();
    const copied = document.execCommand('copy');
    if (!copied) throw new Error('clipboard_copy_failed');
  } finally {
    document.body.removeChild(textArea);
    if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
  }
};

export const writeTextToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (error) {
      // iOS 웹뷰·권한 거부·포커스 잃음 등 → textarea 방식으로 한 번 더 시도
    }
  }
  copyWithTextArea(text);
};

/**
 * 글과 HTML 을 함께 쓴다. ClipboardItem 이 없거나(구형 Firefox·웹뷰) 거부되면 글만 쓴다.
 * @returns {Promise<'html'|'text'>} 실제로 쓴 형식
 */
export const writeClipboard = async ({ plain, html }) => {
  const ClipboardItemCtor = typeof window !== 'undefined' ? window.ClipboardItem : undefined;
  if (html && typeof ClipboardItemCtor === 'function' && typeof navigator.clipboard?.write === 'function') {
    try {
      await navigator.clipboard.write([new ClipboardItemCtor({
        'text/plain': new Blob([plain], { type: 'text/plain' }),
        'text/html': new Blob([html], { type: 'text/html' }),
      })]);
      return 'html';
    } catch (error) {
      // 권한 거부·지원하지 않는 형식 → 글만
    }
  }
  await writeTextToClipboard(plain);
  return 'text';
};
